import { FC, useMemo } from 'react';
import { format } from 'date-fns';
import IconTruck from 'src/presentations/components/organisms/actionDetails/tabsDetails/transportSummary/icons/Truck';
import IconShip from 'src/presentations/components/organisms/actionDetails/tabsDetails/transportSummary/icons/Ship';
import IconAeroplane from 'src/presentations/components/organisms/actionDetails/tabsDetails/transportSummary/icons/Aeroplane';
import IconCourier from 'src/presentations/components/organisms/actionDetails/tabsDetails/transportSummary/icons/Courier';
import IconCt from 'src/presentations/components/organisms/actionDetails/tabsDetails/transportSummary/icons/Ct';
import { beautifyEnum } from 'src/utils/utility';
import { CardLanding } from '../../molecules/card/CardLanding';
import { TransportMedium } from '../../molecules/card/types';
import { ChipInvoiceStatus } from '../../atom/chip/ChipInvoiceStatus';
import { IAuditProvidersProps } from './types';
import { GenericAccordion } from './GenericAccordion';

export const LandingAuditProviders: FC<IAuditProvidersProps> = ({ providers, modality, categoryIdGen, cardIdGen }) => {
  const icon = useMemo(() => {
    const iconProps = {
      color: '#BFBFBF',
      size: 24,
    };

    switch (modality) {
      case 'ROAD':
      case 'RAIL':
        return <IconTruck {...iconProps} />;
      case 'SEA':
        return <IconShip {...iconProps} />;
      case 'AIR':
        return <IconAeroplane {...iconProps} />;
      case 'COURIER':
        return <IconCourier {...iconProps} />;
      case 'CT':
        return <IconCt {...iconProps} />;
      default:
        return null;
    }
  }, [modality]);

  const title = useMemo(() => {
    switch (modality) {
      case 'CT':
        return 'CONTROL TOWER';
      case 'COURIER':
        return 'COURIER';
      default:
        return modality;
    }
  }, [modality]);

  const statsTotal = useMemo(() => providers.reduce((a, b) => a + b.invoiceStatuses.reduce((a2, b2) => a2 + b2?.count || 0, 0), 0), [providers]);

  // Create the status chip for the header
  const headerStatusChip = (
    <div className='relative'>{statsTotal > 0 ? <ChipInvoiceStatus status='FLAGGED' count={statsTotal} /> : <ChipInvoiceStatus status='' />}</div>
  );

  return (
    <GenericAccordion
      id={categoryIdGen(modality.toLowerCase())}
      icon={icon}
      title={title}
      providerCount={providers?.length || 0}
      headerRightContent={headerStatusChip}
    >
      {providers?.length > 0 ? (
        <div className='-mx-12 flex flex-wrap'>
          {providers.map((item) => {
            const queryParams = new URLSearchParams({
              provider: item?.serviceProviderCode,
              modality: item?.modality,
            });

            const constructedUrl = `/dashboard?${queryParams.toString()}`;

            return (
              <div
                id={cardIdGen(`${item?.serviceProviderCode}-${item?.modality}`)}
                key={`provider-${item?.serviceProviderCode}-${item?.serviceProviderName}-${item?.modality}`}
                className='px-8 xl:px-12 w-1/4 mb-16 xl:mb-30'
              >
                <CardLanding
                  title={beautifyEnum(item?.serviceProviderCode)?.toUpperCase()}
                  info={`Last Updated on ${item?.latestInvoiceCreatedDate ? format(new Date(item?.latestInvoiceCreatedDate), 'dd-MMM-yyyy') : '-'}`}
                  transportMedium={item?.modality as TransportMedium}
                  path={constructedUrl}
                >
                  {item?.invoiceStatuses && item?.invoiceStatuses?.length > 0 ? (
                    <>
                      {item?.invoiceStatuses.map((itemStat) => (
                        <div key={`stat-${item?.serviceProviderName}-${item?.modality}-${itemStat?.status}`} className='relative'>
                          <ChipInvoiceStatus status={itemStat?.status} count={itemStat?.count} />
                        </div>
                      ))}
                    </>
                  ) : (
                    <ChipInvoiceStatus status='' />
                  )}
                </CardLanding>
              </div>
            );
          })}
        </div>
      ) : (
        <p className='text-center text-sm'>Not found</p>
      )}
    </GenericAccordion>
  );
};
